import React, { useState, useEffect, useRef } from 'react';

import { DialogUtility } from '@syncfusion/ej2-popups';
import {
    GridComponent,
    Inject,
    Page,
    Selection,
    DetailRow,
    Toolbar,
    Sort,
    ColumnChooser
} from '@syncfusion/ej2-react-grids';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { useToast } from '@hooks';
import 'components/AvaEzm/MemberEnrollment/AdminAndSupport/AdminAndSupport.scss';
import { ControlledSyncfusionTextBox } from 'components/Common/SyncfusionWrappers/Syncfusion';

import { RetryEnrollmentApi } from './RetryEnrollmentApi';
import { EnrollmentGridColumns, EnrollmentResult, selectionSettings, tabsToolbarOptions } from './RetryFailedRuns.Metadata';

const RetryFailedRuns: React.FC = () => {
    const [results, setResults] = useState<EnrollmentResult[]>([]);
    const [selectedEnrollments, setSelectedEnrollments] = useState<EnrollmentResult[]>([]);
    const [pageSettings] = useState({ pageCount: 5, pageSize: 5 });
    const [loading, setLoading] = useState(false);
    const requeueEnrollmentGridRef = useRef<GridComponent>(null);
    const { showErrorToast, showSuccessToast } = useToast();
    const [totalLocked, setTotalLocked] = useState(0);
    const [totalRetryExceeded, setTotalRetryExceeded] = useState(0);
    const [totalInRetryQueue, setTotalInRetryQueue] = useState(0);

    const { handleSubmit, control, formState: { errors }, reset } = useForm<{ enrollmentIds: string }>({
        defaultValues: { enrollmentIds: '' }
    });

    const handleDataGridSelection = () => {
        if (requeueEnrollmentGridRef.current) {
            const selectedRows = requeueEnrollmentGridRef.current.getSelectedRecords() || [];
            setSelectedEnrollments(selectedRows as EnrollmentResult[]);
        }
    };

    const toolbarClick = () => {
        if (selectedEnrollments && selectedEnrollments.length > 0) {
            let confirmDialog: any = null;
            confirmDialog = DialogUtility.confirm({
                animationSettings: { effect: 'None' },
                isModal: true,
                content: `<div>
                    <b>Are you sure you want to requeue the selected enrollments to service bus?</b>
                </div>`,
                cancelButton: {
                    text: 'NO',
                    cssClass: 'e-cancel mt-2',
                    click: () => {
                        confirmDialog.hide();
                    },
                },
                okButton: {
                    text: 'YES',
                    cssClass: 'e-submit mt-2',
                    click: async () => {
                        setLoading(true);
                        confirmDialog.hide();
                        const data = selectedEnrollments.map(enrollment => ({
                            enrollmentID: enrollment.enrollmentID,
                            instanceId: enrollment.instanceId
                        }));
                        try {
                            await RetryEnrollmentApi.RequeueEnrollmentProcess(data);
                            showSuccessToast(`Send the enrollments (${data.map(d => d.enrollmentID).join(', ')}) to service bus successfully`);
                            loadEnrollments();
                        } catch (error) {
                            showErrorToast("Error requeuing enrollments. Please try again.");
                            console.error("Error requeuing enrollments:", error);
                        }
                        setLoading(false);
                    },
                },
                showCloseIcon: true,
                position: { X: 'center', Y: 'center' },
            });
        } else {
            const alertDialog = DialogUtility.alert({
                animationSettings: { effect: 'None' },
                isModal: true,
                closeOnEscape: true,
                content: 'No enrollment was selected.',
                okButton: {
                    text: 'OK',
                    cssClass: 'e-flat e-info',
                    click: () => alertDialog.hide(),
                },
                showCloseIcon: false,
                position: { X: 'center', Y: 'center' },
            });
        }
    };

    const loadEnrollments = async () => {
        try {
            const response = await RetryEnrollmentApi.LoadFailedEnrollments();
            setTotalLocked(response?.totalLocked ?? 0);
            setTotalRetryExceeded(response?.totalRetryExceeded ?? 0);
            setTotalInRetryQueue(response?.totalInRetryQueue ?? 0);
            setResults(response?.transactionHistoryLogs ?? []);
            setSelectedEnrollments([]);
        } catch (error) {
            console.error('Error loading failed enrollments:', error);
        }
    };

    const onSubmit = async (data: { enrollmentIds: string }) => {
        const ids = data.enrollmentIds
            .split(',')
            .map(id => id.trim())
            .filter(id => id.length > 0);

        if (ids.length === 0) {
            DialogUtility.alert({
                animationSettings: { effect: 'None' },
                isModal: true,
                closeOnEscape: true,
                content: 'Please enter at least one Enrollment ID.',
                okButton: {
                    text: 'OK',
                    cssClass: 'e-flat e-info',
                    click: function () { this.hide(); },
                },
                showCloseIcon: false,
                position: { X: 'center', Y: 'center' },
            });
            return;
        }

        const payload = ids.map(enrollmentID => ({
            enrollmentID
        }));

        setLoading(true);
        try {
            const response = await RetryEnrollmentApi.RequeueEnrollmentProcess(payload);
            if (response?.errors) {
                showErrorToast(
                    Object.values(response.errors)
                        .flat()
                        .join('\n')
                );
            } else {
                showSuccessToast(`Send the enrollments (${ids.join(', ')}) to service bus successfully`);
                reset();
                loadEnrollments();
            }
        } catch (error) {
            showErrorToast("Error requeuing enrollments. Please try again.");
            console.error("Error requeuing enrollments:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        void loadEnrollments();
    }, []);

    return (
        <>
            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(255,255,255,0.6)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Spinner
                        animation="border"
                        role="status"
                        style={{
                            width: '4rem',
                            height: '4rem',
                            color: 'rgb(227, 22, 91)',
                        }}
                    />
                </div>
            )}

            <div className='sectionCard'>
                <div className='tilesContainer'>
                    <div className='statTile'>
                        <div className='statTileValue'>{totalLocked.toLocaleString()}</div>
                        <div className='statTileLabel'>Count</div>
                        <div className='statTileTitle'>Failed Enrollments</div>
                    </div>
                    <div className='statTile'>
                        <div className='statTileValue'>{totalRetryExceeded.toLocaleString()}</div>
                        <div className='statTileLabel'>Count</div>
                        <div className='statTileTitle'>Max Retries Exceeded</div>
                    </div>
                    <div className='statTile'>
                        <div className='statTileValue'>{totalInRetryQueue.toLocaleString()}</div>
                        <div className='statTileLabel'>Count</div>
                        <div className='statTileTitle'>Queued for Retry</div>
                    </div>
                </div>
            </div>

            <div className='sectionCard'>
                <h2 className='sectionHeader'>Requeue Records by Enrollment Id</h2>
                <div className='assignEnrollmentsContainer'>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                            <Col className='enrollmentIdLabel'>ENROLLMENT ID(s):</Col>
                            <Col md={9}>
                                <TooltipComponent content={`Input enrollment IDs separated by comma (,)`}>
                                    <div>
                                        <ControlledSyncfusionTextBox
                                            name='enrollmentIds'
                                            control={control}
                                            rules={{
                                                required: 'Enrollment ID(s) is required',
                                            }}
                                        />
                                    </div>
                                    <Form.Text className='text-danger'>{errors.enrollmentIds?.message}</Form.Text>
                                </TooltipComponent>
                            </Col>
                            <Col>
                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    style={{ color: '#fff', minWidth: 120 }}
                                    name='SendToQueue'
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Sending...
                                        </>
                                    ) : (
                                        'SEND TO QUEUE'
                                    )}
                                </button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>

            <div className='sectionCard'>
                <h2 className='sectionHeader'>Retry Failures</h2>
                <div className='gridContainer'>
                    <GridComponent
                        id="requeueGridRef"
                        dataSource={results}
                        ref={requeueEnrollmentGridRef}
                        allowSorting={true}
                        allowPaging={true}
                        pageSettings={pageSettings}
                        toolbar={tabsToolbarOptions as any}
                        showColumnChooser={true}
                        rowSelected={handleDataGridSelection}
                        rowDeselected={handleDataGridSelection}
                        toolbarClick={toolbarClick}
                        selectionSettings={selectionSettings}
                        rowHeight={48}
                        columns={EnrollmentGridColumns}
                        allowTextWrap={true}
                    >
                        <Inject services={[Page, Selection, Sort, Toolbar, ColumnChooser, DetailRow]} />
                    </GridComponent>
                </div>
            </div>
        </>
    );
};

export default RetryFailedRuns;